import { IconTrash } from '@developerz/ui';
import { A, useNavigate, useParams } from '@solidjs/router';
import { useQueryClient } from '@tanstack/solid-query';
import type { JSX } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DashboardRenameModal } from '../components/DashboardRenameModal';
import { DashboardRenderer } from '../components/DashboardRenderer';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { StatusPill } from '../components/StatusPill';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { createDashboardActions } from '../lib/dashboard-actions';
import { DASHBOARDS_QUERY_KEY, useDashboard } from '../lib/dashboards';
import { orgHref } from '../lib/org';
import { SESSION_TOKEN } from '../lib/session';

// One board, rendered from its JSON definition (arc 101 slice 09, task #81) — the
// generic route that replaces a hardcoded view: `/dashboards/:id` loads the detail
// and hands it to <DashboardRenderer/>. Since #1033 it also carries the two writes
// that address THIS board: rename (re-read after) and delete (which leaves the
// route — there is nothing left to render). `token` is injectable for tests
// (mirrors Fleet / Home); the router mounts it with no props → dev session.
//
// A BUILT-IN offers neither: both are a server-side 409 (seeded, shared state),
// so the header says the board is read-only instead of showing dead controls.
export function DashboardView(props: { token?: string | null } = {}): JSX.Element {
  // Undefined-only default: an explicit `null` (signed-out / isolated test render)
  // must reach `useDashboard` unchanged to disable the request — `??` would revive
  // the seeded dev bearer and fetch anyway.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useDashboard(() => params.id, token);
  const queryClient = useQueryClient();
  // Both writes change the INDEX as well as this board, and the index is where
  // Back lands: stale it, or within `staleTime` it lists the old name / the
  // deleted board.
  const staleIndex = (): void =>
    void queryClient.invalidateQueries({ queryKey: [DASHBOARDS_QUERY_KEY, 'list'] });
  const actions = createDashboardActions({
    token,
    onRenamed: () => {
      staleIndex();
      void detail.refetch();
    },
    onDeleted: () => {
      staleIndex();
      navigate(orgHref('/dashboards'));
    },
  });

  return (
    <>
      <A class="dashboard__back" href={orgHref('/dashboards')}>
        <span aria-hidden="true">← </span>
        {t('dashboards.back')}
      </A>
      <QueryBoundary
        query={detail}
        // `null` is the 404 answer (`useDashboard`), and without this the
        // `hasData` gate is true for it — the route would render a header for a
        // board that does not exist and the copy below would be dead. Same
        // wiring as `DataWidget` / `CiView`.
        isEmpty={(d) => d === null}
        loading={<CardGridSkeleton listClass="dashboard-grid" />}
        empty={
          <EmptyState
            message={t('dashboards.not_found')}
            ctaLabel={t('dashboards.back')}
            ctaHref={orgHref('/dashboards')}
          />
        }
      >
        {(board) => {
          // `isEmpty` above already refused `null`; the cast is what tells the
          // typechecker so, and it is the only place that knowledge lives.
          const d = board as NonNullable<typeof board>;
          return (
            <>
              <PageHeader
                title={d.name}
                {...(d.builtin ? { desc: t('dashboards.builtin_readonly') } : {})}
                actions={
                  d.builtin ? (
                    <StatusPill tone="neutral">{t('dashboards.builtin')}</StatusPill>
                  ) : (
                    <div class="dashboards__actions">
                      <Button variant="outline" size="sm" onClick={() => actions.askRename(d)}>
                        {t('dashboards.rename')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => actions.askDelete(d)}>
                        {t('dashboards.delete')}
                      </Button>
                    </div>
                  )
                }
              />
              <DashboardRenderer detail={d} token={token} />
            </>
          );
        }}
      </QueryBoundary>

      <DashboardRenameModal
        open={actions.renameTarget() !== null}
        name={actions.renameTarget()?.name ?? ''}
        pending={actions.pending()}
        error={actions.renameError()}
        onRename={actions.submitRename}
        onClose={actions.close}
      />
      <ConfirmDialog
        open={actions.deleteTarget() !== null}
        title={t('dashboards.delete_title')}
        desc={t('dashboards.delete_body', { name: actions.deleteTarget()?.name ?? '' })}
        media={<IconTrash />}
        cancelLabel={t('dashboards.cancel')}
        actionLabel={t('dashboards.delete_confirm')}
        destructive
        onCancel={actions.close}
        onConfirm={actions.confirmDelete}
      />
      <Toast message={actions.deleteError()} tone="error" onDismiss={actions.clearError} />
    </>
  );
}
